/**
 * A porta entre a tela de pessoas e documentos e a API do módulo 16.
 */
import { pedir } from '@/lib/pedido';

import { CONSULTAS_DE_PESSOAS as C } from './leitura';

export type AplicaA = 'pessoa' | 'equipamento';

/**
 * O estado do documento, calculado no banco pelo `aviso_dias` do tipo.
 *
 * A tela NÃO recalcula: o aviso em dias é parâmetro de cada tipo, e uma
 * segunda conta divergiria na primeira vez que alguém trocasse o número.
 */
export type EstadoDoDocumento = 'sem_validade' | 'sem_data' | 'vencido' | 'vence_logo' | 'valido';

/** Os três motivos de impedimento. Eles impedem igual e se resolvem diferente. */
export type MotivoDoImpedimento = 'ausente' | 'vencido' | 'sem_data';

export interface Impedimento {
  readonly sobre: AplicaA;
  readonly alvoId: string;
  readonly alvo: string;
  readonly tipoCodigo: string;
  readonly tipo: string;
  readonly motivo: MotivoDoImpedimento;
  readonly validoAte: string | null;
  readonly responsavel: string | null;
  readonly obrigatorio: boolean;
  readonly obraId: string | null;
  readonly obra: string | null;
  readonly contratoId: string | null;
  readonly contrato: string | null;
}

export interface DocumentoNaLista {
  readonly id: string;
  readonly tipoId: string;
  readonly tipoCodigo: string;
  readonly tipo: string;
  readonly aplicaA: AplicaA;
  readonly usuarioId: string | null;
  readonly pessoa: string | null;
  readonly equipamentoId: string | null;
  readonly equipamento: string | null;
  readonly numero: string;
  readonly validoAte: string | null;
  readonly responsavelId: string | null;
  readonly responsavel: string | null;
  readonly estado: EstadoDoDocumento;
  readonly diasParaVencer: number | null;
  /** Quantos arquivos do PRÓPRIO documento (0090). Ausente em servidor anterior. */
  readonly arquivos?: number;
  /** Quantas provas de regularização (0090). Ausente em servidor anterior. */
  readonly evidencias?: number;
}

/** O arquivo é a cópia do documento, ou a prova de que a pendência se resolveu (0090). */
export type FinalidadeDoArquivo = 'documento' | 'regularizacao';

export interface ArquivoDoDocumento {
  readonly id: string;
  readonly finalidade: FinalidadeDoArquivo;
  readonly descricao: string;
  readonly mime: string;
  readonly tamanhoBytes: number;
  /** A validade que o documento tinha quando o arquivo entrou (`aaaa-mm-dd`). */
  readonly validoAteNaEpoca: string | null;
  readonly enviadoEm: string;
  readonly enviadoPor: string | null;
}

export interface TipoDeDocumento {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly aplicaA: AplicaA;
  readonly temValidade: boolean;
  readonly avisoDias: number;
  readonly ativo: boolean;
}

export interface Aptidao {
  readonly id: string;
  readonly usuarioId: string;
  readonly pessoa: string;
  readonly modeloId: string | null;
  readonly modelo: string | null;
  readonly configuracaoId: string | null;
  readonly configuracao: string | null;
  readonly desde: string;
  /** Nulo enquanto a autorização está vigente. Encerrar fecha, não apaga. */
  readonly ate: string | null;
  readonly observacao: string;
}

export interface Exigencia {
  readonly id: string;
  readonly tipoId: string;
  readonly tipoCodigo: string;
  readonly tipo: string;
  readonly aplicaA: AplicaA;
  readonly contratoId: string | null;
  readonly contrato: string | null;
  readonly obraId: string | null;
  readonly obra: string | null;
  readonly obrigatorio: boolean;
  readonly validadoEm: string | null;
  readonly validadoPor: string | null;
  readonly observacao: string;
}

/** 0095 · a função que a pessoa exerce — a chave do valor-hora de mão de obra (Custos). */
export interface Funcao {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly ativo: boolean;
  /** Quantas pessoas a exercem hoje. */
  readonly pessoas: number;
}

/** Quem exerce qual função, e desde quando. Encerrar fecha a vigência, não apaga. */
export interface FuncaoDaPessoa {
  readonly id: string;
  readonly usuarioId: string;
  readonly pessoa: string;
  readonly funcaoId: string;
  readonly funcaoCodigo: string;
  readonly funcao: string;
  /** `aaaa-mm-dd`, inclusive. */
  readonly desde: string;
  /** `aaaa-mm-dd`, exclusivo; nulo enquanto vale. */
  readonly ate: string | null;
  readonly registradoPor: string | null;
}

export interface FuncoesDaEmpresa {
  readonly funcoes: readonly Funcao[];
  readonly vinculos: readonly FuncaoDaPessoa[];
  /** As pessoas ativas, para escolher pelo nome. */
  readonly pessoas: readonly { readonly id: string; readonly nome: string }[];
}

export const api = {
  /** A resposta traz a explicação de cada motivo, e a tela a usa. */
  impedimentos: () =>
    pedir<{
      impedimentos: readonly Impedimento[];
      motivos: Readonly<Record<MotivoDoImpedimento, string>>;
    }>(C.impedimentos.rota),
  /** O impedimento de uma obra, para consultar ANTES de alocar. Não barra. */
  impedimentosDaObra: (obraId: string) =>
    pedir<{ impedimentos: readonly Impedimento[]; aviso: string }>(
      `/pessoas/impedimentos/obra/${encodeURIComponent(obraId)}`,
    ),

  /**
   * O impedimento da alocação que AINDA NÃO EXISTE (0066): a máquina, a obra e
   * o contrato escolhidos na tela de alocação. Confere, não barra.
   */
  impedimentosDaAlocacao: (equipamentoId: string, obraId: string, contratoId: string | null) =>
    pedir<{ impedimentos: readonly Impedimento[]; aviso: string }>(
      `/pessoas/impedimentos/alocacao?equipamento=${encodeURIComponent(equipamentoId)}&obra=${encodeURIComponent(obraId)}${
        contratoId ? `&contrato=${encodeURIComponent(contratoId)}` : ''
      }`,
    ),

  documentos: () => pedir<{ documentos: readonly DocumentoNaLista[] }>(C.documentos.rota),
  /** Cobra a renovacao do responsavel pelo documento (0073), uma vez por dia. */
  cobrar: (documentoId: string) =>
    pedir<{ cobrado: boolean; jaCobradoHoje: boolean }>(`/pessoas/documentos/${encodeURIComponent(documentoId)}/cobranca`, { method: 'POST' }),
  /** Os arquivos do documento e as provas de regularização (0090). */
  arquivos: (documentoId: string) =>
    pedir<{ anexos: readonly ArquivoDoDocumento[] }>(`/pessoas/documentos/${encodeURIComponent(documentoId)}/anexos`),
  /** O arquivo vai cru no corpo; a descrição e a finalidade, no endereço. O servidor lê o tipo do conteúdo. */
  anexar: (documentoId: string, arquivo: File, descricao: string, finalidade: FinalidadeDoArquivo) =>
    pedir<{ id: string }>(
      `/pessoas/documentos/${encodeURIComponent(documentoId)}/anexos?descricao=${encodeURIComponent(descricao)}&finalidade=${finalidade}`,
      { method: 'POST', body: arquivo, headers: { 'content-type': arquivo.type || 'application/octet-stream' } },
    ),
  /** Uma URL que vale alguns minutos, e não o arquivo. */
  urlDoArquivo: (anexoId: string) =>
    pedir<{ url: string; expiraEmSegundos: number; mime: string }>(`/pessoas/anexos/${encodeURIComponent(anexoId)}/arquivo`),
  tipos: () => pedir<{ tipos: readonly TipoDeDocumento[] }>(C.tipos.rota),
  aptidoes: () => pedir<{ aptidoes: readonly Aptidao[] }>(C.aptidoes.rota),
  /** Quem pode ser autorizado a operar: só para quem grava aptidão (`aptidao.gravar`). */
  elegiveis: () =>
    pedir<{ pessoas: readonly { readonly id: string; readonly nome: string; readonly email: string | null }[] }>(C.elegiveis.rota),
  exigencias: () => pedir<{ exigencias: readonly Exigencia[] }>(C.exigencias.rota),

  criarTipo: (corpo: {
    readonly codigo: string;
    readonly nome: string;
    readonly aplicaA: AplicaA;
    readonly temValidade?: boolean;
    readonly avisoDias?: number;
  }) => pedir<{ id: string }>('/pessoas/tipos-de-documento', { method: 'POST', body: JSON.stringify(corpo) }),

  gravarDocumento: (corpo: {
    readonly tipoId: string;
    readonly usuarioId?: string | null;
    readonly equipamentoId?: string | null;
    readonly numero?: string;
    readonly emitidoEm?: string | null;
    readonly validoAte?: string | null;
    readonly responsavelId?: string | null;
    readonly observacao?: string;
  }) => pedir<{ id: string }>('/pessoas/documentos', { method: 'POST', body: JSON.stringify(corpo) }),

  autorizar: (corpo: {
    readonly usuarioId: string;
    readonly modeloId?: string | null;
    readonly configuracaoId?: string | null;
    readonly observacao?: string;
  }) => pedir<{ id: string }>('/pessoas/aptidoes', { method: 'POST', body: JSON.stringify(corpo) }),
  encerrarAptidao: (id: string) =>
    pedir<{ id: string }>(`/pessoas/aptidoes/${encodeURIComponent(id)}/encerramento`, { method: 'POST' }),

  criarExigencia: (corpo: {
    readonly tipoId: string;
    readonly contratoId?: string | null;
    readonly obraId?: string | null;
    readonly obrigatorio?: boolean;
    readonly observacao?: string;
  }) => pedir<{ id: string }>('/pessoas/exigencias', { method: 'POST', body: JSON.stringify(corpo) }),
  funcoes: () => pedir<FuncoesDaEmpresa>(C.funcoes.rota),
  criarFuncao: (corpo: { codigo: string; nome: string }) =>
    pedir<{ id: string }>('/pessoas/funcoes', { method: 'POST', body: JSON.stringify(corpo) }),
  desativarFuncao: (id: string, motivo: string) =>
    pedir<{ id: string }>(`/pessoas/funcoes/${encodeURIComponent(id)}/desativacao`, { method: 'POST', body: JSON.stringify({ motivo }) }),
  definirFuncaoDaPessoa: (corpo: { usuarioId: string; funcaoId: string; desde: string; motivo: string }) =>
    pedir<{ id: string }>('/pessoas/funcoes-das-pessoas', { method: 'POST', body: JSON.stringify(corpo) }),
  encerrarFuncaoDaPessoa: (id: string, motivo: string) =>
    pedir<{ id: string }>(`/pessoas/funcoes-das-pessoas/${encodeURIComponent(id)}/encerramento`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),
  validarExigencia: (id: string) =>
    pedir<{ id: string }>(`/pessoas/exigencias/${encodeURIComponent(id)}/validacao`, { method: 'POST' }),
};
